import { visibleMenu } from "@/menu";

/**
 * The Inventory workspace's navigation, read from the one list the panel already keeps (menu.js, the group with
 * workspace: "inventory"): its dividers are Kubi's sections, the pages under each are that section's tabs. So the
 * workspace shows exactly the pages this person may open, under the same rules as the rest of Connect (role
 * permissions, hidden pages, "All stores").
 */

// Kubi's section names (menu.js dividers) and the line icon (InvIcon) each shows.
export const AREA_ICON = {
  Overview: "overview",
  Inventory: "boxes",
  "Menu & Recipes": "recipes",
  Purchasing: "cart",
  Cost: "coins",
  Administration: "shield",
};

export const PAGE_ICON = {
  "inventory-dashboard": "overview",
  "inventory-stock-health": "health",
  "inventory-alerts": "bell",
  "stock-month-report": "report",
  "inventory-ingredients": "list",
  "inventory-products": "pizza",
  "inventory-count": "clipboard",
  "inventory-variance": "scale",
  "inventory-waste-log": "trash",
  "inventory-transfers": "swap",
  "inventory-preps": "bowl",
  "inventory-dough": "dough",
  "inventory-toppings": "layers",
  "inventory-packaging": "package",
  "inventory-menu-engineering": "quadrant",
  "inventory-purchasing": "cart",
  "inventory-draft-pos": "file-pen",
  "inventory-receiving": "truck",
  "inventory-invoices": "receipt",
  "inventory-price-drift": "trend",
  "inventory-vendors": "store",
  "inventory-cogs": "coins",
  "inventory-cogs-variance": "scale",
  "inventory-users": "users",
  "inventory-first-list": "upload",
  "inventory-audit": "history",
};

// Pages that belong to the workspace but have no tab of their own (opened from another page).
const INNER_PAGES = { "inventory-po-editor": "inventory-draft-pos" };

const hiddenPages = () =>
  (import.meta.env.VUE_APP_HIDDEN_PAGES || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/** The workspace's group in menu.js, as this person sees it (null when they may open none of it). */
function group(store) {
  const utype = store.state.auth && store.state.auth.current && store.state.auth.current.user ? store.state.auth.current.user.utype : undefined;
  const menu = visibleMenu(utype, hiddenPages(), store.state.rbac.me.permissions, { allStores: store.getters.isAllStores });
  return menu.find((item) => item.workspace === "inventory") || null;
}

/**
 * [{ title, icon, pages: [{ title, name, query, icon, children: [...] }] }]: Kubi's sections with the pages this
 * person may open. A page marked `child` (the Dough tabs) goes under the page before it.
 */
export function inventoryAreas(store) {
  const g = group(store);
  if (!g) return [];
  const areas = [];
  let area = null;
  for (const item of g.subMenu || []) {
    if (item.divider) {
      area = { title: item.title, icon: AREA_ICON[item.title] || "boxes", pages: [] };
      areas.push(area);
      continue;
    }
    if (!area || !item.name) continue;
    if (item.child) {
      const parent = area.pages[area.pages.length - 1];
      if (parent) (parent.children = parent.children || []).push({ ...item });
      continue;
    }
    area.pages.push({ ...item, icon: PAGE_ICON[item.name] || area.icon });
  }
  return areas.filter((a) => a.pages.length);
}

/** Route names that open inside the workspace: its pages, their inner pages, and every inventory-* route. */
export function isWorkspaceRoute(name) {
  if (!name) return false;
  return name.startsWith("inventory-") || name === "stock-month-report" || !!INNER_PAGES[name];
}

/** Where a route sits: { area, page } (page = the tab to mark; an inner page marks the page it belongs to). */
export function locate(areas, route) {
  const name = INNER_PAGES[route.name] || route.name;
  for (const area of areas) {
    for (const page of area.pages) {
      if (page.name === name) return { area, page };
    }
  }
  return { area: null, page: null };
}

/** The first page of an area, as a router location. */
export function areaTarget(area) {
  const p = area.pages[0];
  return p.query ? { name: p.name, query: p.query } : { name: p.name };
}

/** A page as a router location. */
export function pageTarget(page) {
  return page.query ? { name: page.name, query: page.query } : { name: page.name };
}
